import {
  type CoverageSnapshot,
  type BenefitDetailAmounts,
  type Amounts,
  type PlanLevel,
} from 'schemas';
import { ACTIVE_COVERAGE_STATUS } from './memberSliceConstants';
import { PlanConfig } from './memberPlanSlice';
import { PlanAmounts } from './selectMemberPlan';
import { userHasMetDeductible, userHasMetOutOfPocketMax } from '../ui/uiUtils';

/**
 * Accepts a coverage snapshot from Availity, as serialized by fusion, and returns the requested values object, or null
 */
export function getPlanAmounts(
  memberPlan: CoverageSnapshot | null,
  planType: PlanLevel,
  amountType: keyof BenefitDetailAmounts
): Amounts | null {
  if (!memberPlan || !Array.isArray(memberPlan.plans)) return null;

  const activePlan = memberPlan.plans.find((plan) => plan.status === ACTIVE_COVERAGE_STATUS);
  // According to the API docs, notApplicableNetwork includes benefits that apply regardless of network
  // https://developer.availity.com/blog/2025/3/25/hipaa-transactions#c_coverages_references__benefitdetail__entry__1
  const amounts =
    activePlan?.accumulations?.[amountType]?.inNetwork ||
    activePlan?.accumulations?.[amountType]?.notApplicableNetwork;
  if (Array.isArray(amounts)) {
    return amounts.find((amount) => amount.level === planType) || null;
  }
  return null;
}

/**
 * Set OOP and Deductible accumulations based on the accumulation met status
 */
export function getPlanConfigAccumulations(
  planConfig: PlanConfig | null,
  accumulationMetStatus: string
): Pick<
  PlanAmounts,
  | 'individualOutOfPocketMaxAccumulation'
  | 'individualDeductibleAccumulation'
  | 'familyDeductibleAccumulation'
  | 'familyOutOfPocketMaxAccumulation'
> {
  if (!planConfig)
    return {
      individualOutOfPocketMaxAccumulation: 0,
      individualDeductibleAccumulation: 0,
      familyDeductibleAccumulation: 0,
      familyOutOfPocketMaxAccumulation: 0,
    };
  const isDeductibleMet = userHasMetDeductible(accumulationMetStatus);
  const isOopMaxMet = userHasMetOutOfPocketMax(accumulationMetStatus);
  return {
    individualDeductibleAccumulation: isDeductibleMet ? planConfig.individualDeductible : 0,
    familyDeductibleAccumulation: isDeductibleMet ? planConfig.familyDeductible : 0,
    // eslint-disable-next-line no-nested-ternary
    individualOutOfPocketMaxAccumulation: isOopMaxMet
      ? planConfig.individualOutOfPocketMax
      : isDeductibleMet
      ? planConfig.individualDeductible
      : 0,
    // eslint-disable-next-line no-nested-ternary
    familyOutOfPocketMaxAccumulation: isOopMaxMet
      ? planConfig.familyOutOfPocketMax
      : isDeductibleMet
      ? planConfig.familyDeductible
      : 0,
  };
}

/** Utility functions for differentiating 0 from other falsy values */
export const isFiniteNumber = (a: unknown): boolean => Number.isFinite(a);
export const getFiniteNumber = (a: unknown): number | undefined =>
  isFiniteNumber(a) ? (a as number) : undefined;

export const getRemaining = (
  total: number | undefined,
  amount: number | undefined
): number | undefined => {
  if (!isFiniteNumber(total) || !isFiniteNumber(amount)) return undefined;
  return Math.max(0, (total as number) - (amount as number));
};
